// Fleet catalog — powers /fleet and /fleet/$slug pages for each vehicle.

export type FleetImageKey = "aviator" | "suburban" | "sprinter";

export interface FleetRecord {
  slug: string;
  imageKey: FleetImageKey;
  tag: string;
  name: string;
  fullName: string;
  headline: string;
  summary: string;
  intro: string;
  capacity: string;
  luggage: string;
  passengers: number;
  serviceName: string;
  serviceType: string;
  schemaDescription: string;
  imageCaption: string;
  offerTiers: { key: string; name: string; price: number; description: string }[];
  features: string[];
  bestFor: string[];

  relatedServices: string[];
  relatedOccasions: string[];
  alt: string;
  faqs: { q: string; a: string }[];
}

export const FLEET_VEHICLES: FleetRecord[] = [
  {
    slug: "lincoln-aviator-luxury-suv",
    imageKey: "aviator",
    tag: "Luxury Midsize SUV",
    name: "Lincoln Aviator",
    fullName: "Lincoln Aviator Luxury Midsize SUV",
    headline: "Lincoln Aviator Luxury Midsize SUV Chauffeur Service",
    summary:
      "Black Lincoln Aviator luxury midsize SUV chauffeured for individuals, couples and small executive parties across the San Francisco Bay Area.",
    intro:
      "The Lincoln Aviator is our default executive vehicle — a black midsize luxury SUV with heated leather seating, a whisper-quiet cabin and low, easy step-in for airport transfers, hosted client visits, dinner rides and private-aviation arrivals at SFO, SJC, OAK and the Bay Area FBOs.",
    capacity: "Up to 3 passengers",
    luggage: "Up to 3 standard bags",
    passengers: 3,
    serviceName: "Lincoln Aviator Luxury Midsize SUV Chauffeur Service",
    schemaDescription:
      "Black Lincoln Aviator luxury midsize SUV chauffeur service for individuals, couples and small executive parties across the San Francisco Bay Area.",
    imageCaption: "Black Lincoln Aviator luxury midsize SUV used for private chauffeur transportation",
    serviceType: "Private luxury SUV chauffeur transportation",
    offerTiers: [
      {
        key: "offer",
        name: "Luxury Sedan hourly chauffeur service",
        price: 130,
        description:
          "$130 per hour with a five-hour minimum for Silicon Valley hourly bookings.",
      },
    ],

    features: [
      "Black exterior, black leather interior with wood and metal trim",
      "Individual climate control, heated rear seats and privacy-friendly cabin",
      "Wi-Fi and phone charging available on request",
      "Quiet cabin acoustics for phone calls and confidential discussion",
      "Meets executive dress-code and vehicle-standard requirements for hosted visits",
    ],
    bestFor: [
      "Single-executive airport transfers",
      "Client dinners and hosted visits",
      "Anniversary and milestone dinner rides",
      "Private-aviation arrivals at San Jose Jet Center, Signature and Atlantic",
    ],
    relatedServices: [
      "airport-transfers",
      "corporate-transportation",
      "hourly-chauffeur",
      "black-car-service",
      "private-jet-fbo-transfers",
    ],
    relatedOccasions: [
      "business-meetings-client-dinners",
      "anniversaries-birthdays",
    ],
    alt: "Side profile of a black Lincoln Aviator luxury SUV with a uniformed chauffeur standing beside the vehicle, staged for Silicon Valley executive and airport transfers",
    faqs: [
      {
        q: "How many passengers fit in the Lincoln Aviator?",
        a: "Up to three passengers in comfort. For four or more, or for groups with more than three checked bags, the Chevrolet Suburban LT is the right upgrade.",
      },
      {
        q: "Is the Aviator suitable for airport transfers?",
        a: "Yes. It's our most-booked airport vehicle — quiet cabin for a rested arrival, three bags of luggage capacity and a chauffeur who meets you inside at SFO, SJC or OAK on international arrivals when requested.",
      },
    ],
  },
  {
    slug: "chevrolet-suburban-lt",
    imageKey: "suburban",
    tag: "Full-Size SUV",
    name: "Chevrolet Suburban LT",
    fullName: "Chevrolet Suburban LT Full-Size SUV",
    headline: "Chevrolet Suburban LT Full-Size SUV Chauffeur Service",
    summary:
      "A black Chevrolet Suburban LT full-size SUV chauffeured for executive teams, families with luggage and small groups across the Bay Area.",
    intro:
      "The Chevrolet Suburban LT is the standard full-size SUV for executive teams of four to six, families with checked luggage and small groups heading to Napa, Monterey or a home football game. Space for six passengers, room for six checked bags and the same discreet chauffeur presence you get in the Aviator.",
    capacity: "Up to 6 passengers",
    luggage: "Up to 6 standard bags",
    passengers: 6,
    serviceName: "Chevrolet Suburban LT Full-Size SUV Chauffeur Service",
    schemaDescription:
      "Black Chevrolet Suburban LT full-size SUV chauffeur service for executive teams, families with luggage and small groups across the San Francisco Bay Area.",
    imageCaption: "Black Chevrolet Suburban LT full-size SUV used for private chauffeur transportation",
    serviceType: "Private full-size SUV chauffeur transportation",
    offerTiers: [
      {
        key: "offer",
        name: "Chevrolet Suburban LT hourly chauffeur service",
        price: 150,
        description:
          "$150 per hour with a five-hour minimum for Silicon Valley hourly bookings.",
      },
    ],

    features: [
      "Black exterior, black leather interior",
      "Individual climate control front and rear",
      "USB charging at every seating row",
      "Rear cargo area sized for six standard checked bags",
      "Suitable for executive-team travel, family airport transfers and sport-event arrivals",
    ],
    bestFor: [
      "Executive-team airport transfers from SFO, SJC and OAK",
      "Family and multi-generation airport travel with luggage",
      "Sports-event tailgate and premium-seat arrivals",
      "Wine-country day trips for one or two couples",
      "Wedding-party family transportation",
    ],
    relatedServices: [
      "airport-transfers",
      "corporate-transportation",
      "group-transportation",
      "hourly-chauffeur",
      "special-events",
    ],
    relatedOccasions: [
      "weddings-receptions",
      "corporate-offsites",
      "concerts-theater",
      "wine-tours",
    ],
    alt: "Side profile of a black Chevrolet Suburban LT full-size SUV with a uniformed chauffeur standing beside the vehicle, staged for Bay Area corporate groups, weddings and airport transfers",
    faqs: [
      {
        q: "How many bags does the Suburban LT hold?",
        a: "Up to six standard checked bags plus carry-ons for six passengers. For groups with more bags or larger equipment, the Sprinter van's rear cargo area is the better fit.",
      },
      {
        q: "Is the Suburban LT priced hourly?",
        a: "Hourly Suburban LT bookings are $150 per hour with a five-hour minimum in Silicon Valley. Point-to-point pricing is quoted per trip through our online booking page.",
      },
    ],
  },
  {
    slug: "mercedes-benz-sprinter",
    imageKey: "sprinter",
    tag: "Group Van",
    name: "Mercedes-Benz Sprinter",
    fullName: "Mercedes-Benz Sprinter Passenger Van",
    headline: "Mercedes-Benz Sprinter Passenger Van Chauffeur Service",
    summary:
      "Black Mercedes-Benz Sprinter passenger van chauffeured for groups, weddings and wine-country tours up to 14 passengers.",
    intro:
      "The Mercedes-Benz Sprinter is the fleet's group vehicle — one chauffeur, one vehicle, up to 14 passengers and rear luggage space sized for a full corporate roadshow or wedding party. We configure Sprinters in three tiers — up to 6, 7–10 and 11–14 passengers — with the pricing that matches, so groups pay for the vehicle they actually need.",
    capacity: "6 to 14 passengers",
    luggage: "Up to 14 standard bags, subject to configuration",
    passengers: 14,
    serviceName: "Mercedes-Benz Sprinter Passenger Van Chauffeur Service",
    schemaDescription:
      "Black Mercedes-Benz Sprinter passenger van chauffeur service for groups, weddings, corporate transportation and wine-country tours, with configurations for up to 14 passengers.",
    imageCaption: "Black Mercedes-Benz Sprinter passenger van used for chauffeured group transportation",
    serviceType: "Chauffeured group transportation",
    offerTiers: [
      {
        key: "offer-6-passenger",
        name: "Sprinter chauffeur service for up to 6 passengers",
        price: 175,
        description:
          "$175 per hour with a five-hour minimum for Silicon Valley hourly bookings.",
      },
      {
        key: "offer-7-10-passengers",
        name: "Sprinter chauffeur service for 7 to 10 passengers",
        price: 200,
        description:
          "$200 per hour with a five-hour minimum for Silicon Valley hourly bookings.",
      },
      {
        key: "offer-11-14-passengers",
        name: "Sprinter chauffeur service for 11 to 14 passengers",
        price: 225,
        description:
          "$225 per hour with a five-hour minimum for Silicon Valley hourly bookings.",
      },
    ],

    features: [
      "Black exterior with black executive-configured interior",
      "Individual captain's chairs with USB charging",
      "Rear cargo area for full-group luggage",
      "Suitable for conventions, weddings, wine tours and sport-event group nights",
      "Available in 6-passenger, 7–10 passenger and 11–14 passenger configurations",
    ],
    bestFor: [
      "Corporate roadshows and conference delegations",
      "Wedding-party and guest-shuttle transportation",
      "Napa and Sonoma wine-country group tours",
      "Group airport transfers from SFO, SJC and OAK",
      "49ers, Warriors, Sharks and Giants group game-night arrivals",
    ],
    relatedServices: [
      "group-transportation",
      "hourly-chauffeur",
      "special-events",
      "convention-transportation",
      "wine-country-tours",
    ],
    relatedOccasions: [
      "corporate-offsites",
      "weddings-receptions",
      "wine-tours",
      "convention-conference-transportation",
      "concerts-theater",
      "prom-graduation",
    ],
    alt: "Side profile of a black Mercedes-Benz Sprinter passenger van with a uniformed chauffeur standing beside the vehicle, configured for 11 to 14 passenger group transportation across the San Francisco Bay Area",
    faqs: [
      {
        q: "What are the Sprinter hourly rates and minimums?",
        a: "Sprinter hourly rates start at $175/hr for up to 6 passengers, $200/hr for 7–10 passengers and $225/hr for 11–14 passengers, each with a five-hour minimum in Silicon Valley.",
      },
      {
        q: "Can the Sprinter handle wedding-party transportation?",
        a: "Yes. The Sprinter is our default wedding-party and guest-shuttle vehicle, coordinated with the ceremony and reception timeline your planner sets.",
      },
    ],
  },
];

export const FLEET_BY_SLUG: Record<string, FleetRecord> = Object.fromEntries(
  FLEET_VEHICLES.map((v) => [v.slug, v]),
);

export function getFleetVehicle(slug: string) {
  return FLEET_BY_SLUG[slug];
}

/**
 * Centralized, approved passenger tiers for the Mercedes-Benz Sprinter.
 * Every page that quotes Sprinter capacity or hourly pricing must read from
 * here so the tiers can never drift apart again.
 */
export const SPRINTER_TIERS = [
  { label: "Up to 6 passengers", passengers: "up to 6", hourly: 175 },
  { label: "7–10 passengers", passengers: "7–10", hourly: 200 },
  { label: "11–14 passengers", passengers: "11–14", hourly: 225 },
] as const;

/**
 * Compact hourly rate list used by the homepage pricing section and /rates.
 * Rendered as a real HTML table so the figures can be lifted into featured
 * snippets, voice answers and comparison panels.
 */
